// @ts-strict-ignore
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import cloneDeep from "lodash-es/cloneDeep";
import * as C from "../../shared/constants/globals.constants";
import { ProfileElementSchema } from "src/app/modules/esim/shared/models/profile-element.model";
import { ProfileService } from "./profile.service";
import { ValidatorService } from "./validator.service";
import { EWizValidator } from "../constants/validators.constants";
import { ExpertScreensGeneratorService } from "src/app/modules/esim/shared/services/expert-screens-generator.service";
import { ExpertValidatorService } from "src/app/modules/esim/shared/services/expert-validator.service";
import { APIModel, APIService } from "src/app/api/shared/services/api.service";
import { ProfileDto } from "../models/profile.model";
import { ExpertModeProfileDto } from "../models/expert-profile.model";
import { AppState } from "src/app/ngrx/root.reducer";
import { APIActions } from "src/app/api/ngrx/api.actions.types";

@Injectable({ providedIn: "root" })
export class BootstrapService {
  constructor(
    private store: Store<AppState>,
    private apiService: APIService,
    private profileService: ProfileService,
    private validatorService: ValidatorService,
    private expertScreensGeneratorService: ExpertScreensGeneratorService,
    private expertValidatorService: ExpertValidatorService
  ) {}

  _showPerformanceLogging = false;

  getEWizProfileState(profile: APIModel.ESimProfileDto): string {
    return this.validatorService.validateNodeTree(EWizValidator, C.EWIZ_ROOT, profile.profileJson as ProfileDto);
  }

  getExpertProfileState(profile: APIModel.ESimProfileDto, schema: ProfileElementSchema): string {
    const expertModeScreens = this.expertScreensGeneratorService.generateExpertModeScreens(
      cloneDeep(profile.profileJson as ExpertModeProfileDto),
      schema
    );

    return this.expertValidatorService.buildProfileState(expertModeScreens);
  }

  isStatusSet(profile: APIModel.ESimProfileDto): boolean {
    if (!profile.status || !profile.statusUpdatedDate) {
      return false;
    } else {
      return true;
    }
  }

  bootstrapProfiles(esimProfiles: APIModel.ESimProfileDto[], allSchemas: ProfileElementSchema[], expertModeUser: boolean) {
    return new Observable((observer) => {
      performance.clearMarks();
      performance.clearMeasures();
      performance.mark("bootstrapProfiles_start");

      const newProfiles: any[] = [];
      const updatedStatusProfiles: APIModel.ProfileStatusUpdateDto[] = [];

      for (const profile of esimProfiles) {
        if (!profile.profileType || profile.profileType === "EWiz") {
          newProfiles.push({
            ...profile,
            createdDate: new Date(profile.createdDate),
            updatedDate: new Date(profile.updatedDate),
            status: this.isStatusSet(profile) ? profile.status : this.getEWizProfileState(profile),
            profileJson: this.profileService.generateProfileSettings(profile.profileJson)
          });

          if (!this.isStatusSet(profile)) {
            updatedStatusProfiles.push({
              id: profile.id,
              status: newProfiles.at(-1).status
            });
          }
        } else if (profile.profileType === "Expert") {
          if (expertModeUser) {
            const profileJson = profile.profileJson as ExpertModeProfileDto;

            let schemaFound = false;

            for (const schema of allSchemas) {
              if (schema.key === profileJson.schemaKey) {
                schemaFound = true;

                let expertModeProfileOverallState = null;

                if (this.isStatusSet(profile)) {
                  expertModeProfileOverallState = profile.status;
                } else {
                  expertModeProfileOverallState = this.getExpertProfileState(profile, schema);
                }
                newProfiles.push({
                  ...profile,
                  createdDate: new Date(profile.createdDate),
                  updatedDate: new Date(profile.updatedDate),
                  status: expertModeProfileOverallState
                });

                if (!this.isStatusSet(profile)) {
                  updatedStatusProfiles.push({
                    id: profile.id,
                    status: newProfiles.at(-1).status
                  });
                }

                break;
              }
            }

            if (!schemaFound) {
              // Commenting this out for now, it is causing too much log clutter
              /*this.logService.log(
                `[ESimProfilesComponent] bootstrapProfiles() could not resolve expert profile "${profile.name}" to a schema configuration with key ${profileJson.schemaKey}; hiding profile from view.`
              );*/
            }
          }
        }
      }

      performance.mark("bootstrapProfiles_end");
      performance.measure("bootstrapProfiles", "bootstrapProfiles_start", "bootstrapProfiles_end");

      if (this._showPerformanceLogging) {
        console.warn(`bootstrapProfiles() time: ${performance.getEntriesByName("bootstrapProfiles")[0].duration}ms`);
      }

      if (updatedStatusProfiles.length > 0) {
        console.warn(`Encountered ${updatedStatusProfiles.length} profiles that need initial status seeding`);

        this.apiService.ESimProfile_updateProfileStates(updatedStatusProfiles).subscribe({
          next: (response) => {
            this.store.dispatch(
              APIActions.UpdateESimProfiles({
                profiles: [...response]
              })
            );

            observer.next(newProfiles);
            observer.complete();
          },
          error: () => {
            observer.next(newProfiles);
            observer.complete();
          }
        });
      } else {
        observer.next(newProfiles);
        observer.complete();
      }
    });
  }
}
